//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import Cuid from '@models/Cuid';
import { PRODUCT_ERROR_FILTER_VALUE } from '@tagging/constants/taggingConstants';
import {
  BulkPublishableTaggingFeedType,
  ITaggingFeedItem,
  ITaggingSettings,
  TaggingFeedCounts,
  TaggingFeedType,
} from '@tagging/models/taggingModels';
import { shimItemType } from '@tagging/tagging.utils';
import { gatewayFetch } from '@utils/sessionFetch';

export interface IFeedMediaResponse {
  data: {
    items: ITaggingFeedItem[];
    totalCount: number;
  };
}

export interface IRemoveFeedMediaResponse {
  data: {
    totalCount: number;
  };
}

export interface IPublishAllFeedMediaResponse {
  data: {
    totalCount: number;
  };
}

export interface IFeedSettingsResponse {
  data: ITaggingSettings;
}

export interface IFeedSettingsErrorResponse {
  error: unknown;
}

export interface IFeedCountResponse {
  data: {
    itemCounts: TaggingFeedCounts;
  };
}

export interface IFeedGenerationResponse {
  data: {
    taskId: string;
  };
}

export interface IForceUpdateItemsResponse {
  data: {
    updatedCount: number;
  };
}

function gatewayFeedFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<Response> {
  return gatewayFetch(
    'feed_gateway',
    `/api/v1/feeds/google-asset-feed${endpoint}`,
    init,
  );
}

/**
 * Gets the media associated with this client's Google Asset Feed
 */
export async function getFeedItems(
  feedType: TaggingFeedType,
  productError: PRODUCT_ERROR_FILTER_VALUE,
  offset: number,
  limit?: number,
  searchText?: string,
): Promise<IFeedMediaResponse> {
  const hasProductErrorFilter = productError !== PRODUCT_ERROR_FILTER_VALUE.ALL;

  const queryParams = $.param({
    limit,
    offset,
    searchText,
    status: feedType,
    ...(hasProductErrorFilter && {
      productError,
    }),
  });

  const res = await gatewayFeedFetch(`/media?${queryParams}`);
  const json = (await res.json()) as IFeedMediaResponse;

  return {
    ...json,
    data: {
      ...json.data,
      items: shimItemType(json.data.items, feedType),
    },
  };
}

/**
 * Gets the settings associated with this client's Google Asset Feed
 */
export async function getSettings(): Promise<
  IFeedSettingsResponse | IFeedSettingsErrorResponse
> {
  const res = await gatewayFeedFetch('');
  return await res.json();
}

/**
 * Gets the number of items associated with each TaggingFeedType
 */
export async function getFeedCounts(): Promise<IFeedCountResponse> {
  const res = await gatewayFeedFetch('/media/item-counts');
  return await res.json();
}

/**
 * Saves the settings for this client's Google Asset Feed
 */
export async function saveSettings(settings: ITaggingSettings): Promise<void> {
  const res = await gatewayFeedFetch('', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
  return await res.json();
}

/**
 * Removes the provided items from the Google Asset Feed streams and moderation queues
 */
export async function removeFeedItems(
  cuids: Cuid[],
): Promise<IRemoveFeedMediaResponse> {
  const res = await gatewayFeedFetch('/media', {
    method: 'DELETE',
    body: JSON.stringify({
      cuids,
    }),
  });
  return await res.json();
}

/**
 * Publishes the provided items from the Google Asset Feed streams and moderation queues
 */
export async function publishAllFeedItems(
  feedType: BulkPublishableTaggingFeedType,
): Promise<void> {
  const queryParams = $.param({
    status: feedType,
  });

  await gatewayFeedFetch(`/media?${queryParams}`, {
    method: 'PUT',
  });
}

/**
 * Publishes the provided items from the Google Asset Feed streams and moderation queues
 */
export async function publishFeedItems(
  cuids: Cuid[],
  feedType: TaggingFeedType,
): Promise<IPublishAllFeedMediaResponse> {
  const queryParams = $.param({
    status: feedType,
  });
  const res = await gatewayFeedFetch(`/media/items?${queryParams}`, {
    method: 'PUT',
    body: JSON.stringify({
      cuids,
    }),
  });
  return await res.json();
}

export async function startFeedGeneration(): Promise<IFeedGenerationResponse> {
  const res = await gatewayFeedFetch('/generate-feed', {
    method: 'PUT',
  });

  return await res.json();
}

export async function forceUpdateItems(
  cuids: Cuid[],
): Promise<IForceUpdateItemsResponse> {
  const res = await gatewayFeedFetch('/force-update-items', {
    method: 'PUT',
    body: JSON.stringify({
      cuids,
    }),
  });

  return await res.json();
}
